import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client/react';
import { EyeOutlined } from '@ant-design/icons';
import { Flex, Form, Radio, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';

import DatePicker from 'components/DatePicker/DatePicker';
import useModalControls from 'hooks/useModalControls';
import { AUTHORIZATION_HISTORY, TRANSACTIONS } from 'queries/transactions';
import { formatDateToSubmit } from 'utilities/datesAndTimes';
import { formatTransactionData } from 'utilities/helpers';

import {
  resetTransactionSelections,
  setTransactionsSelected,
  useTransactionState,
} from './transactionState';
import DeclinedAuthorizationControl from './DeclinedAuthorizationControl';
import FeeWaiverHistoryControl from './FeeWaiverHistoryControl';
import OTBEligibilityControl from './OTBEligibilityControl';
import FraudAuthorizationReviewModal from './FraudAuthorizationReviewModal';
import ViewSelectedTransactionsModal from './ViewSelectedTransactionsModal';
import TransactionTable from '../TransactionTable/TransactionTable';

const { RangePicker } = DatePicker;

const VIEW_OPTIONS = {
  TRANSACTIONS: 'TRANSACTIONS',
  AUTHORIZATIONS: 'AUTHORIZATIONS', // 13 week history of authorizations
};

const TransactionSection = () => {
  const { accountId, customerId } = useParams();
  const flags = useFlags();
  const { selectedTransactionKeys } = useTransactionState();
  const [feeWaiverEligible, setFeeWaiverEligible] = useState(false);
  const [otbReleaseEligible, setOTBReleaseEligible] = useState(false);

  const previous90Days = [dayjs().subtract(90, 'day'), dayjs()];
  const previous14Weeks = [dayjs().subtract(14, 'week'), dayjs()];

  const { isFdi, isFdr, enableOtbEligibility } = flags;

  const [form] = Form.useForm();
  const viewOption = Form.useWatch('viewOption', form);
  const [dateRange, setDateRange] = useState(previous90Days);
  const fraudAuthorizationReviewModal = useModalControls();
  const viewSelectedTransactionsModal = useModalControls();

  const selectionActions = useMemo(() => {
    const actions = [
      {
        text: <EyeOutlined />,
        onClick: viewSelectedTransactionsModal.show,
        tooltip: {
          text: 'View selected transactions',
        },
      },
    ];
    if (isFdi || isFdr) {
      actions.push({
        text: 'Review Fraud',
        onClick: fraudAuthorizationReviewModal.show,
      });
    }
    return actions;
  }, [isFdi, isFdr]);

  const transactionsQuery = useQuery(TRANSACTIONS, {
    notifyOnNetworkStatusChange: true,
    skip: viewOption !== VIEW_OPTIONS.TRANSACTIONS,
    variables: {
      accountId,
      customerId,
      fromDate: formatDateToSubmit(dateRange[0]),
      toDate: formatDateToSubmit(dateRange[1]),
    },
  });

  const transactions = useMemo(() => {
    const activities = transactionsQuery.data?.account?.activities || [];
    return activities.length
      ? formatTransactionData(activities, dateRange[0], dateRange[1])
      : [];
  }, [transactionsQuery.data]);

  const authorizationHistoryQuery = useQuery(AUTHORIZATION_HISTORY, {
    notifyOnNetworkStatusChange: true,
    skip: viewOption !== VIEW_OPTIONS.AUTHORIZATIONS,
    fetchPolicy: 'no-cache',
    variables: {
      accountId,
      fromDate: formatDateToSubmit(dateRange[0]),
      toDate: formatDateToSubmit(dateRange[1]),
    },
  });

  const authorizationHistory = useMemo(() => {
    let fraudAuthorizations =
      authorizationHistoryQuery.data?.fraudAuthorizations || [];
    return fraudAuthorizations.length
      ? formatTransactionData(fraudAuthorizations)
      : [];
  }, [authorizationHistoryQuery.data]);

  const onViewOptionChange = (option) => {
    switch (option.target.value) {
      case VIEW_OPTIONS.TRANSACTIONS:
        setDateRange(previous90Days);
        form.setFieldValue('dateRange', previous90Days);
        break;
      case VIEW_OPTIONS.AUTHORIZATIONS:
        setDateRange(previous14Weeks);
        form.setFieldValue('dateRange', previous14Weeks);
        break;
      default:
    }
  };

  useEffect(() => {
    resetTransactionSelections();
  }, [customerId, accountId]);

  let loading = false;
  let activity = [];

  if (viewOption === VIEW_OPTIONS.AUTHORIZATIONS) {
    loading = authorizationHistoryQuery.loading;
    activity = authorizationHistory;
  }

  if (viewOption === VIEW_OPTIONS.TRANSACTIONS) {
    loading = transactionsQuery.loading;
    activity = transactions;
  }

  return (
    <>
      <FraudAuthorizationReviewModal {...fraudAuthorizationReviewModal} />
      <ViewSelectedTransactionsModal {...viewSelectedTransactionsModal} />

      <Typography.Title level={5}>
        {viewOption === VIEW_OPTIONS.AUTHORIZATIONS
          ? 'Authorizations'
          : 'Transactions'}
      </Typography.Title>
      <Form
        form={form}
        initialValues={{
          viewOption: VIEW_OPTIONS.TRANSACTIONS,
          dateRange: previous90Days,
        }}
        name="transactions"
      >
        <Flex justify="space-between" wrap="wrap" gap="var(--spacing-sm)">
          <Flex gap="var(--spacing-xs)">
            <Form.Item name="viewOption" noStyle>
              <Radio.Group onChange={onViewOptionChange} disabled={loading}>
                <Radio.Button
                  data-testid="transactionsRadio"
                  value={VIEW_OPTIONS.TRANSACTIONS}
                >
                  Transactions
                </Radio.Button>
                <Radio.Button
                  data-testid="authorizationsRadio"
                  value={VIEW_OPTIONS.AUTHORIZATIONS}
                >
                  Authorizations
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Tooltip
              title={
                viewOption === VIEW_OPTIONS.AUTHORIZATIONS
                  ? 'Authorization history is only available for this date range'
                  : ''
              }
            >
              <div>
                <Form.Item name="dateRange" noStyle>
                  <RangePicker
                    allowEmpty={[false, false]}
                    data-testid="transactionDateRange"
                    disabled={viewOption === VIEW_OPTIONS.AUTHORIZATIONS}
                    onChange={(date) => date && setDateRange(date)}
                    style={{ width: '220px' }}
                  />
                </Form.Item>
              </div>
            </Tooltip>
          </Flex>
          <Flex
            gap="var(--spacing-xs)"
            align="flex-start"
            style={{ marginLeft: 'auto' }}
          >
            <FeeWaiverHistoryControl />
            {enableOtbEligibility ? (
              <OTBEligibilityControl
                transactions={transactions}
                onEligibilityChange={setOTBReleaseEligible}
              />
            ) : undefined}

            <DeclinedAuthorizationControl />
          </Flex>
        </Flex>
      </Form>
      <TransactionTable
        enableOTBRelease={enableOtbEligibility ? otbReleaseEligible : true}
        showAdditionalActions={true}
        transactions={activity}
        loading={loading}
        rowSelection={{
          onSelect: (transaction, isSelected) => {
            setTransactionsSelected([transaction], isSelected);
          },
          onSelectAll: (isSelected, _, updated) => {
            setTransactionsSelected(updated, isSelected);
          },
          onSelectMultiple: (isSelected, _, changeRows) => {
            setTransactionsSelected(changeRows, isSelected);
          },
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedTransactionKeys,
        }}
        selectionActions={selectionActions}
      />
    </>
  );
};

export default TransactionSection;
